import type { SortDirection } from "@kaleidoscope-solutions/engine-wasm"
import type { RemovableRef } from "@vueuse/core"
import { useLocalStorage } from "@vueuse/core"
import { defineStore } from "pinia"
import { ModelLogLevel } from "@/utils/useEngineModel"

interface State {
  logLevel: RemovableRef<ModelLogLevel>
  showSelectMessage: RemovableRef<boolean>
  autoImportDefaultModel: RemovableRef<boolean> // latch to avoid a reload loop
  navOpened: RemovableRef<boolean>
  myoLayoutOpened: RemovableRef<boolean> // For `Make your own` view
  showIdentifiers: RemovableRef<boolean> // Show UUIds for dashboard items
  closedBanners: RemovableRef<string[]> // List of closed banners
  autoImportOnModelDirty: RemovableRef<boolean> // Auto import when model is dirty
  showHiddenPages: RemovableRef<boolean> // Show hidden pages in nav
  widgetsCollapsed: RemovableRef<string[]> // List of collapsed widget IDs
  chartSort: RemovableRef<Record<string, keyof typeof SortDirection>> // List of collapsed widget IDs
}

export const useSettingsStore = defineStore("settings", {
  state: (): State => ({
    logLevel: useLocalStorage<ModelLogLevel>("log_level", ModelLogLevel.Info),
    showSelectMessage: useLocalStorage<boolean>("locate_message", true),
    autoImportDefaultModel: useLocalStorage<boolean>(
      "auto_import_default",
      true
    ),
    navOpened: useLocalStorage<boolean>("nav_opened", true),
    myoLayoutOpened: useLocalStorage<boolean>("myo_layout_opened", true),
    closedBanners: useLocalStorage<string[]>("closedBanners", []),
    showIdentifiers: useLocalStorage<boolean>("show_identifiers", false),
    autoImportOnModelDirty: useLocalStorage<boolean>("auto_import_on_model_dirty", false),
    showHiddenPages: useLocalStorage<boolean>("show_hidden_pages", false),
    widgetsCollapsed: useLocalStorage<string[]>("widgets_collapsed", []),
    chartSort: useLocalStorage<Record<string, keyof typeof SortDirection>> ("chart_sort", {}),
  }),
  getters: {
    defaultModel(): string {
      const defaultModel = import.meta.env.VITE_DEFAULT_MODEL
      if (defaultModel && defaultModel.length > 0) {
        return defaultModel
      }
      return ""
    },
    shouldImportDefaultModel(): boolean {
      return this.autoImportDefaultModel && this.defaultModel.length > 0
    },
  },
  actions: {
    resetSettings() {
      this.showSelectMessage = true
      this.autoImportDefaultModel = true
      this.navOpened = true
      this.myoLayoutOpened = true
      this.closedBanners = []
      this.widgetsCollapsed = []
    },
    toggleNavOpened() {
      this.navOpened = !this.navOpened
    },
    resetBannerList() {
      this.closedBanners = []
    },
    addClosedBanner(id: string) {
      if (!this.closedBanners.includes(id)) {
        this.closedBanners.push(id)
      }
    },
    removeClosedBanner(id: string) {
      this.closedBanners = this.closedBanners.filter((bannerId) => bannerId !== id)
    },
    setWidgetCollapsed(id: string, collapsed: boolean) {
      if (collapsed) {
        if (!this.widgetsCollapsed.includes(id)) {
          this.widgetsCollapsed.push(id)
        }
      } else {
        this.widgetsCollapsed = this.widgetsCollapsed.filter((widgetId) => widgetId !== id)
      }
    },
    isWidgetCollapsed(id: string) {
      return this.widgetsCollapsed.includes(id)
    },
    toggleWidgetExpanded(id: string) {
      this.setWidgetCollapsed(id, !this.isWidgetCollapsed(id))
    }
  },
})
